// ─────────────────────────────────────────────
//  onCallWindows — the on-call CRNA's rows and the 24-hour coverage maths.
//
//  Pure — no React, no Firestore. Extracted from TimelineOnCallCard for the
//  same reason MdCallEditControls was extracted from TimelineMdCallRail: the
//  card, the edit modal and the tests all need this, and the rule that decides
//  whether OB has a hole in its cover should be readable on its own.
//
//  ⚠️ THIS INVENTS NO STORE. The on-call CRNA already IS an assignment row with
//  `isOnCall: true` (src/domain/coverage.js), carrying `orRoom`, `ahpId`,
//  `ahpName`, `startTime`, `endTime`.
// ─────────────────────────────────────────────
import { to12h, hmToMinutes, isCaselessWindow } from "../../domain/timeline";

export const DAY_MIN = 1440;

// The importer's full-day on-call convention (features/sheet-import/
// buildBoardWrites.js): 07:00 → 07:00 = 7 AM to 7 AM the next morning.
export const FULL_DAY_START = "07:00";
export const FULL_DAY_END = "07:00";

// 24 hours in 15-minute steps. NOT `START_TIME_OPTIONS` (06:00–19:00): an
// on-call window legitimately runs to 7 AM the next morning, and a list that
// cannot express it would make the normal case unreachable.
export const ON_CALL_TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 0; h < 24; h += 1) {
    for (const m of [0, 15, 30, 45]) {
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return out;
})();

export const sameRoom = (a, b) =>
  String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

// Every `isOnCall` row on the board, in array order. Order is the write order
// too, so row 1 stays row 1 across an edit.
export function allOnCallRows(assignments) {
  return (Array.isArray(assignments) ? assignments : []).filter((a) => a && a.isOnCall);
}

export function onCallRowsFor(orRoom, assignments) {
  if (!orRoom) return [];
  return allOnCallRows(assignments).filter((a) => sameRoom(a.orRoom, orRoom));
}

// WHICH ROOM THIS CARD IS ABOUT.
//
// Normally the answer is on the board already: the first on-call row's own
// `orRoom`. With NO on-call rows there is nothing to read, and that is exactly
// the state the card must be loudest about — so it falls back to a room whose
// name looks like OB, which is the room this rule exists for. When neither
// exists the card says so rather than naming a room that is not there.
export function primaryOnCallRoom(assignments, rooms) {
  const first = allOnCallRows(assignments)[0];
  if (first?.orRoom) return first.orRoom;
  const ob = (Array.isArray(rooms) ? rooms : []).find((r) =>
    /\bob\b/i.test(String(r?.name || ""))
  );
  return ob?.name || "";
}

// "7 AM–3 PM", or "" when either end is missing/unparseable. The board's own
// `to12h`, so this reads exactly as the axis beside it does — a second 12-hour
// formatter here would drift from the chart the same people are looking at.
export function windowLabel(row) {
  const s = to12h(hmToMinutes(row?.startTime));
  const e = to12h(hmToMinutes(row?.endTime));
  return s && e ? `${s}–${e}` : "";
}

// A row that covers the whole clock: start === end (the 07:00→07:00
// convention), or no times at all.
//
// ⚠️ DELEGATED, NOT DUPLICATED. `domain/timeline.isCaselessWindow` asks the same
// question of the same fields — it is the test that decides whether the chart
// draws a bar for this row. This helper used to carry its own copy of the maths;
// one of the two would eventually be "fixed" alone, and the card would then
// disagree with the chart about whether a CRNA was on watch all day.
export function isFullDay(row) {
  return isCaselessWindow(row);
}

// ── THE GAP CHECK ───────────────────────────────────────────────────────────
//
// Paints each window onto a 1440-minute circle and returns the first uncovered
// stretch, or null when the day is whole. A CIRCLE rather than a number line,
// because an on-call window wrapping past midnight is the ordinary case here —
// 3 PM to 7 AM is not "inverted", it is the night shift, and an `end > start`
// check would reject the normal way this room is staffed.
//
// `windows` = [{ startMin, endMin }]. start === end means the full 24 hours.
export function coverageGap(windows) {
  const covered = new Array(DAY_MIN).fill(false);
  let painted = false;
  for (const w of windows || []) {
    const s = w?.startMin;
    const e = w?.endMin;
    if (s == null || e == null) continue;
    painted = true;
    const len = s === e ? DAY_MIN : (((e - s) % DAY_MIN) + DAY_MIN) % DAY_MIN;
    for (let i = 0; i < len; i += 1) covered[(s + i) % DAY_MIN] = true;
  }
  // Nothing to judge. The caller has already refused a half-filled form; a
  // "gap" invented from missing times would be a second, vaguer error.
  if (!painted) return null;
  const first = covered.indexOf(false);
  if (first === -1) return null;
  let startMin = first;
  while (!covered[(startMin - 1 + DAY_MIN) % DAY_MIN]) {
    startMin = (startMin - 1 + DAY_MIN) % DAY_MIN;
    if (startMin === first) break;
  }
  let endMin = first;
  while (!covered[endMin]) {
    endMin = (endMin + 1) % DAY_MIN;
    if (endMin === startMin) break;
  }
  return { startMin, endMin };
}

// Is `min` inside this window (circular, end-exclusive)?
export function windowCovers(w, min) {
  const s = w?.startMin;
  const e = w?.endMin;
  if (s == null || e == null || min == null) return false;
  if (s === e) return true;
  const len = (((e - s) % DAY_MIN) + DAY_MIN) % DAY_MIN;
  return (((min - s) % DAY_MIN) + DAY_MIN) % DAY_MIN < len;
}

// ── VALIDATION ──────────────────────────────────────────────────────────────
//
// Returns ONE blocking sentence, or null. Every branch NAMES the obstacle: a
// greyed-out Save on a clinical form is indistinguishable from a broken one,
// and the gap case in particular has to say which hours nobody is covering —
// "invalid times" would leave the operator to find the hole themselves.
export function validateOnCall({ mode, slots = [], roomName }) {
  const room = roomName || "this room";
  if (!roomName) {
    return "There is no on-call room on today's board, so there is nobody to put on call.";
  }
  const [a, b] = slots;
  if (!a?.ahpId) return "Pick the CRNA who is on call.";
  if (mode !== "two") return null;

  if (!b?.ahpId) return "Pick the second CRNA, or switch back to one.";
  if (a.ahpId === b.ahpId) {
    return `${a.name || "That CRNA"} cannot cover both halves of the day. Pick a different second CRNA.`;
  }
  const windows = [a, b].map((w) => ({
    startMin: hmToMinutes(w.startTime),
    endMin: hmToMinutes(w.endTime),
  }));
  if (windows.some((w) => w.startMin == null || w.endMin == null)) {
    return "Both CRNAs need a start and an end time.";
  }
  const gap = coverageGap(windows);
  if (gap) {
    return `Nobody covers ${room} between ${to12h(gap.startMin)} and ${to12h(gap.endMin)}.`;
  }
  return null;
}
